import React, { Suspense, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import "./App-layout.css";
import AppSideBar from "../appSideBar/AppSideBar";
import AppHeader from "../appHeader/AppHeader";
import AppMobileDock from "../appMobileDock/AppMobileDock";
import { useRelatedOperationModal } from "../../hooks/useRelatedOperationModal";
import { popupsAreOpen } from "../popUpContainer/PopUpContainer";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import { NotificationsProvider } from "../../context/NotificationsContext";

// Layout route (see Routing.js) — mounted once for every protected page and
// never torn down between them, so the sidebar/header persist across
// navigation instead of remounting on every route change. Pages render
// through <Outlet/> in place of the old {props.children}.
function AppLayout() {
  // The desktop rail's width is pure CSS (:hover). The only shell state left
  // is the mobile drawer, which has no CSS-only equivalent.
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // The drawer covers the whole viewport; letting the page behind it scroll
  // leaves the user somewhere unexpected when it closes.
  //
  // Unlocking defers to popupsAreOpen(): a popup writes this same property, so
  // clearing it unconditionally on drawer close — or on this layout's cleanup —
  // unlocked the page underneath a modal that was still open. Only the last
  // holder of the lock gets to release it.
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = "hidden";
    } else if (!popupsAreOpen()) {
      document.body.style.overflow = "";
    }
    return () => {
      if (!popupsAreOpen()) {
        document.body.style.overflow = "";
      }
    };
  }, [isMobileOpen]);

  // Owned here, not by AppHeader: AppHeader is position:fixed with its own
  // stacking context, which would trap the popup and paint it below the
  // sidebar, unlike every other modal in the app.
  const { openRelated, relatedModal } = useRelatedOperationModal();

  // NotificationsProvider sits here, like the seller app's: this layout mounts
  // after sign in, so the provider starts with a token and polls. At the app
  // root it mounted on the login page, found no token and never started.
  // Everything that reads it (header, bell, sidebar, pages, popups) is inside.
  return (
    <NotificationsProvider>
      <AppSideBar
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
      />

      <div className="main-app-content">
        <AppHeader
          isMobileOpen={isMobileOpen}
          setIsMobileOpen={setIsMobileOpen}
          onOpenRelated={openRelated}
        />

        {/* Pages are lazy chunks (Routing.js): the shell stays and this
            spinner fills the content area while a page's code arrives. */}
        <div className="app-content"><Suspense fallback={<LoadingSpinner />}><Outlet /></Suspense></div>

        <AppMobileDock />

        {/* Dismisses the drawer on tap-outside. Only rendered while open so it
            never intercepts clicks on the desktop rail. */}
        {isMobileOpen && (
          <div className="sidebar-overlay" onClick={() => setIsMobileOpen(false)} />
        )}

        {relatedModal}
      </div>
    </NotificationsProvider>
  );
}

export default AppLayout;
